import {
  DeviceTypeEnum,
  SCREEN_WIDTH_BREAKPOINTS,
} from "@/constants/constants";
import { StaticImport } from "next/dist/shared/lib/get-img-props";
import Image, { getImageProps } from "next/image";
import { MouseEventHandler, ReactEventHandler, forwardRef } from "react";

interface ImgProps {
  src: string | StaticImport;
  smallScreenSrc?: string | StaticImport;
  alt: string;
  fill?: boolean;
  className?: string;
  height?: number | `${number}`;
  width?: number | `${number}`;
  showFallbackImage?: boolean;
  objectFit?: string | undefined;
  sizes?: string;
  priority?: boolean | undefined;
  useNativeImage?: boolean;
  loading?: "lazy" | "eager";
  onClick?: MouseEventHandler<HTMLImageElement>;
  onError?: ReactEventHandler<HTMLImageElement> | undefined;
}

/**
 * Responsive Image Component with support for desktop and small device sources.
 *
 * @component
 * @param {Object} props - Component properties
 * @param {string} props.src - URL for the main image (desktop size).
 * @param {string} [props.smallDeviceSrc] - URL for the image optimized for smaller devices. If you want to use a different image URL for mobile screens, use this prop.
 * **Use Case**: For example, if there is a requirement to display vertical thumbnails
 * for mobile screens and horizontal thumbnails for desktop screens, you can provide
 * the vertical image URL here, while keeping the horizontal image URL in `src`.
 * @param {string} props.alt - Alternative text for the image (required for accessibility).
 * @param {boolean} [props.fill=false] - Whether the image should fill its container.
 * @param {string} [props.className=""] - Additional CSS classes for the image.
 * @param {number} [props.height] - Height of the image (required if `fill` is false).
 * @param {number} [props.width] - Width of the image (required if `fill` is false).
 * @param {boolean} [props.showFallbackImage=false] - Adds a fallback styling class to the image.
 * @param {"cover" | "contain" | "fill" | "none" | "scale-down"} [props.objectFit] - How the image should fit within its container.
 * @param {string} [props.sizes] - Sizes attribute for responsive image loading.
 * @param {boolean} [props.priority=false] - Whether the image should be prioritized for loading (Next.js specific).
 * @param {boolean} [props.useNativeImage=false] - Whether the image should be loaded using the native image component.
 * @param {Function} [props.onClick] - Callback function triggered on image click.
 * @param {Function} [props.onError] - Callback function triggered on image load error.
 *
 * @returns {JSX.Element} A responsive image element using <picture> and <img>.
 *
 * */
export const Img = forwardRef<HTMLImageElement, ImgProps>(
  (
    {
      src,
      smallScreenSrc: smallScreenSrc,
      alt,
      fill,
      className = "",
      height,
      width,
      showFallbackImage,
      objectFit,
      sizes,
      priority,
      useNativeImage,
      loading = "lazy",
      onClick,
      onError,
    },
    ref,
  ) => {
    const finalClassNames = `${className} ${showFallbackImage ? " fallback-image" : ""} ${
      objectFit ? `object-${objectFit}` : ""
    } ${useNativeImage && fill ? "!w-full !h-full" : ""}`;

    // Prepare common properties for both desktop and mobile sources
    const common = {
      alt,
      fill,
      className: finalClassNames,
      onError,
      onClick,
      width,
      height,
      sizes,
      priority,
      fetchPriority: priority ? ("high" as const) : ("auto" as const),
    };

    if (useNativeImage && typeof src === "string") {
      return (
        <img
          src={src}
          {...common}
          loading={loading}
          fetchPriority={priority ? "high" : "auto"}
          ref={ref}
        />
      );
    }

    if (!smallScreenSrc) {
      // If no `smallDeviceSrc` is provided, return a simple Image component
      return <Image src={src} {...common} ref={ref} />;
    }

    // Remove fetchPriority from common for picture element to avoid prop passing issues
    const { fetchPriority: _fetchPriority, ...commonWithoutFetchPriority } =
      common;

    const {
      props: { srcSet: desktop },
    } = getImageProps({
      ...commonWithoutFetchPriority,
      src,
    });
    const {
      props: { srcSet: mobile, ...rest },
    } = getImageProps({
      ...commonWithoutFetchPriority,
      src: smallScreenSrc,
    });

    return (
      <picture>
        <source
          media={`(min-width: ${SCREEN_WIDTH_BREAKPOINTS[DeviceTypeEnum.DESKTOP].min}px)`}
          srcSet={desktop}
          sizes={sizes}
        />
        <source media="(min-width: 0px)" srcSet={mobile} sizes={sizes} />
        <img {...rest} ref={ref} />
      </picture>
    );
  },
);

Img.displayName = "Img";
